import React, { useState, useRef, useEffect } from "react";
import { cn } from "./utils";

interface DropdownProps {
  children: React.ReactNode;
  trigger: React.ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  className?: string;
}

export function CustomDropdown({
  children,
  trigger,
  align = "end",
  side = "bottom",
  sideOffset = 4,
  className,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getPositionClasses = () => {
    const baseClasses = "absolute z-[9999] !bg-white";

    switch (side) {
      case "top":
        return `${baseClasses} bottom-full mb-${sideOffset}`;
      case "right":
        return `${baseClasses} left-full ml-${sideOffset}`;
      case "left":
        return `${baseClasses} right-full mr-${sideOffset}`;
      default: // bottom
        return `${baseClasses} top-full mt-${sideOffset}`;
    }
  };

  const getAlignmentClasses = () => {
    switch (align) {
      case "start":
        return "left-0";
      case "center":
        return "left-1/2 transform -translate-x-1/2";
      default: // end
        return "right-0";
    }
  };

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer"
      >
        {trigger}
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(
            getPositionClasses(),
            getAlignmentClasses(),
            "bg-background border  shadow-lg rounded-lg min-w-[8rem]",
            className
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

interface DropdownItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: "default" | "destructive";
}

export function DropdownItem({
  children,
  onClick,
  className,
  variant = "default",
}: DropdownItemProps) {
  const baseClasses =
    "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors";
  const variantClasses = {
    default: "hover:bg-accent text-foreground",
    destructive: "hover:bg-destructive/10 text-destructive",
  };

  return (
    <div
      className={cn(baseClasses, variantClasses[variant], className)}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

interface DropdownSeparatorProps {
  className?: string;
}

export function DropdownSeparator({ className }: DropdownSeparatorProps) {
  return <div className={cn("border-t border-border my-1", className)} />;
}

interface DropdownContentProps {
  children: React.ReactNode;
  className?: string;
}

export function DropdownContent({ children, className }: DropdownContentProps) {
  return <div className={cn("py-1", className)}>{children}</div>;
}
